
import { Link } from "react-router-dom";
import { Edit, Building, ArrowLeft, Network, Trash2, GitMerge, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OrganizationWithRelations } from "@/types/custom-types";
import { OrganizationBreadcrumb } from "./OrganizationBreadcrumb";
import { ClientFieldsGuide } from "./ClientFieldsGuide";
import { ActionMenu, ActionItem } from "@/components/ui/action-menu";

interface OrganizationHeaderProps {
  organization: OrganizationWithRelations;
  id: string;
  onDelete?: () => void;
  onMerge?: () => void;
}

export function OrganizationHeader({ 
  organization, 
  id, 
  onDelete, 
  onMerge 
}: OrganizationHeaderProps) {
  
  const organizationActions: ActionItem[] = [
    {
      label: "Edit Organization",
      icon: <Edit className="h-4 w-4" />,
      href: `/organizations/${id}/edit`
    },
    {
      label: "View Hierarchy",
      icon: <Network className="h-4 w-4" />,
      href: `/organization-hierarchy/${id}`
    },
    {
      label: "Merge Organization",
      icon: <GitMerge className="h-4 w-4" />,
      onClick: onMerge
    },
    {
      label: "Delete Organization",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: onDelete,
      variant: "destructive"
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-2 md:flex-row md:justify-between md:space-y-0">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/organizations">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Organizations</span>
            </Link>
          </Button>
          <OrganizationBreadcrumb organizationId={id} />
        </div>
        <div className="flex items-center space-x-2">
          <ClientFieldsGuide />
          <Button variant="outline" asChild>
            <Link to={`/organization-hierarchy/${id}`}>
              <Network className="mr-2 h-4 w-4" />
              View Hierarchy
            </Link>
          </Button>
          <Button asChild>
            <Link to={`/organizations/${id}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Organization
            </Link>
          </Button>
          <ActionMenu 
            label="Organization Actions"
            actions={organizationActions}
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col space-y-4 md:flex-row md:items-start md:justify-between md:space-y-0">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building className="h-5 w-5" />
                <h1 className="text-2xl font-bold">{organization.name}</h1>
                {organization.status && (
                  <Badge variant={organization.status === "active" ? "outline" : "secondary"}>
                    {organization.status}
                  </Badge>
                )}
                {organization.tier && (
                  <Badge variant="secondary">
                    {organization.tier}
                  </Badge>
                )}
                {organization.client_type && (
                  <Badge variant="secondary">
                    {organization.client_type}
                  </Badge>
                )}
              </div>
              {organization.industry && (
                <p className="text-sm text-muted-foreground">Industry: {organization.industry}</p>
              )}
              {organization.website && (
                <p className="text-sm text-muted-foreground">
                  Website:{" "}
                  <a
                    href={organization.website.startsWith("http") ? organization.website : `https://${organization.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    {organization.website}
                  </a>
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
